"use client"

import { DomainAwareLogin } from "@/components/auth/domain-aware-login"
import { useAuth } from "@/lib/auth"
import { useEffect, useState } from "react"
import { getSessionMessage, clearSessionMessage } from "@/lib/utils"
import { useTheme } from "@/lib/theme-context"
import { getBackgroundClasses, getTextClasses } from "@/lib/theme-utils"

export default function HomePage() {
  const { user, session, isLoading } = useAuth()
  const [sessionMessage, setSessionMessage] = useState<string | null>(null)
  const { theme } = useTheme()

  useEffect(() => {
    // Check for session message on component mount
    const message = getSessionMessage()
    if (message) {
      setSessionMessage(message)
      clearSessionMessage() // Clear it after reading
    }
  }, [])

  // Do NOT redirect to /dashboard here - AuthEnforcer canonicalizes / -> /t/{slug}/dashboard.
  // Redirecting here caused a loop: root pushes /dashboard -> dashboard mounts, sees !user (stale) -> pushes / -> repeat.

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className={`min-h-screen ${getBackgroundClasses(theme, 'page')} flex items-center justify-center`}>
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className={`absolute inset-0 border-4 rounded-full ${theme === 'dark' ? 'border-cyan-500/30' : 'border-cyan-600/30'} animate-ping`}></div>
            <div className={`absolute border-4 rounded-full inset-2 ${theme === 'dark' ? 'border-t-cyan-500' : 'border-t-cyan-600'} border-r-transparent border-b-transparent border-l-transparent animate-spin`}></div>
          </div>
          <h2 className={`text-xl font-semibold ${getTextClasses(theme)} mb-2`}>
            Loading
          </h2>
          <p className={getTextClasses(theme, 'muted')}>
            Please wait while we check your authentication status.
          </p>
        </div>
      </div>
    )
  }

  // Show login page if not authenticated
  if (!user || !session) {
    return <DomainAwareLogin sessionMessage={sessionMessage} />
  }

  // Authenticated: show nothing; AuthEnforcer will canonicalize URL to /t/{slug}/dashboard
  return null
}
